import { http } from '@beelday/common'
import { RoomAddress } from 'common/types'
import { useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { YouTubeStreamDefinition, YouTubeStreamId } from './model'
import { Config } from 'common/config'

export type YouTubeStreamApi = {
	create: (
		address: RoomAddress,
		definition: YouTubeStreamDefinition
	) => Promise<void>
	current: (address: RoomAddress, youtubeId: YouTubeStreamId) => Promise<void>
	removeCurrent: (address: RoomAddress) => Promise<void>
	play: (address: RoomAddress, offset: number) => Promise<void>
	pause: (address: RoomAddress, offset: number) => Promise<void>
	finish: (address: RoomAddress) => Promise<void>
	requestOffset: (address: RoomAddress) => Promise<void>
	provideOffset: (address: RoomAddress, offset: number) => Promise<void>
	all: (address: RoomAddress) => Promise<Array<YouTubeStreamDefinition>>
}

export const createYouTubeStreamingApi = (
	serverUrl: string
): YouTubeStreamApi => ({
	create: (address, definition) =>
		fetch(
			`${serverUrl}/api/v0/workflows/${address.workflowId}/interactionschemes/${address.interactionSchemeId}/rooms/${address.roomId}/features/youtube/create/${definition.id}`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'PB-Correlation-Id': uuidv4().replaceAll('-', '').slice(0, 16),
				},
				body: JSON.stringify({
					...definition,
					type: 'YOUTUBE',
				}),
			}
		).then(http.checkStatus),
	current: (address, youtubeId) =>
		fetch(
			`${serverUrl}/api/v0/workflows/${address.workflowId}/interactionschemes/${address.interactionSchemeId}/rooms/${address.roomId}/features/youtube/current/${youtubeId}`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'PB-Correlation-Id': uuidv4().replaceAll('-', '').slice(0, 16),
				},
			}
		).then(http.checkStatus),
	removeCurrent: address =>
		fetch(
			`${serverUrl}/api/v0/workflows/${address.workflowId}/interactionschemes/${address.interactionSchemeId}/rooms/${address.roomId}/features/youtube/current`,
			{
				method: 'DELETE',
				headers: {
					'Content-Type': 'application/json',
					'PB-Correlation-Id': uuidv4().replaceAll('-', '').slice(0, 16),
				},
			}
		).then(http.checkStatus),
	play: (address, offset) =>
		fetch(
			`${serverUrl}/api/v0/workflows/${address.workflowId}/interactionschemes/${address.interactionSchemeId}/rooms/${address.roomId}/features/youtube/play/${offset}`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'PB-Correlation-Id': uuidv4().replaceAll('-', '').slice(0, 16),
				},
			}
		).then(http.checkStatus),
	pause: (address, offset) =>
		fetch(
			`${serverUrl}/api/v0/workflows/${address.workflowId}/interactionschemes/${address.interactionSchemeId}/rooms/${address.roomId}/features/youtube/pause/${offset}`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'PB-Correlation-Id': uuidv4().replaceAll('-', '').slice(0, 16),
				},
			}
		).then(http.checkStatus),
	finish: address =>
		fetch(
			`${serverUrl}/api/v0/workflows/${address.workflowId}/interactionschemes/${address.interactionSchemeId}/rooms/${address.roomId}/features/youtube/finish`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'PB-Correlation-Id': uuidv4().replaceAll('-', '').slice(0, 16),
				},
			}
		).then(http.checkStatus),
	requestOffset: address =>
		fetch(
			`${serverUrl}/api/v0/workflows/${address.workflowId}/interactionschemes/${address.interactionSchemeId}/rooms/${address.roomId}/features/youtube/requestOffset`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'PB-Correlation-Id': uuidv4().replaceAll('-', '').slice(0, 16),
				},
			}
		).then(http.checkStatus),
	provideOffset: (address, offset) =>
		fetch(
			`${serverUrl}/api/v0/workflows/${address.workflowId}/interactionschemes/${address.interactionSchemeId}/rooms/${address.roomId}/features/youtube/provideOffset/${offset}`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'PB-Correlation-Id': uuidv4().replaceAll('-', '').slice(0, 16),
				},
			}
		).then(http.checkStatus),
	all: address =>
		fetch(
			`${serverUrl}/api/v0/workflows/${address.workflowId}/interactionschemes/${address.interactionSchemeId}/rooms/${address.roomId}/features/youtube/all`,
			{
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					'PB-Correlation-Id': uuidv4().replaceAll('-', '').slice(0, 16),
				},
			}
		)
			.then(http.checkStatus)
			.then(http.parseBody),
})

export const useYouTubeStreamingApi = (): YouTubeStreamApi => {
	return useMemo(() => createYouTubeStreamingApi(Config.beeldayBackendUrl), [])
}

export default useYouTubeStreamingApi
